import React, {
    useState,
    useEffect,
    useReducer,
    useContext,
    useRef,
    useMemo,
} from "react";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";

import { makeStyles, useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Avatar from "@material-ui/core/Avatar";
import {
    Facebook as FacebookIcon,
    Instagram as InstagramIcon,
    MessageCircle as WhatsAppIcon,
    Search as SearchIcon,
    Users as GroupIcon,
    Trash2 as DeleteOutlineIcon,
    Pencil as EditIcon,
    CheckCircle2 as CheckCircleIcon,
    XCircle as CancelIcon,
    Ban as BlockIcon,
    ChevronDown as ArrowDropDown,
    UploadCloud as Backup,
    Smartphone as ContactPhone,
} from "lucide-react";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import Box from "@material-ui/core/Box";

import IconButton from "@material-ui/core/IconButton";

import api from "../../services/api";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ContactModal from "../../components/ContactModal";
import ConfirmationModal from "../../components/ConfirmationModal";

import { i18n } from "../../translate/i18n";
import MainContainer from "../../components/MainContainer";
import HubPageHeader from "../../components/HubPageHeader";
import toastError from "../../errors/toastError";

import { AuthContext } from "../../context/Auth/AuthContext";
import { Can } from "../../components/Can";
import NewTicketModal from "../../components/NewTicketModal";
import { TagsFilter } from "../../components/TagsFilter";
import { useSearch } from "../../context/Search/SearchContext";
import PopupState, { bindTrigger, bindMenu } from "material-ui-popup-state";
import { v4 as uuidv4 } from "uuid";

import { Menu, MenuItem, Chip, Typography, Drawer } from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { Filter as FilterIcon, X as XIcon, Sparkles, HelpCircle } from "lucide-react";
import AppTooltip from "../../components/AppTooltip";

import ContactImportWpModal from "../../components/ContactImportWpModal";
import useCompanySettings from "../../hooks/useSettings/companySettings";
import { TicketsContext } from "../../context/Tickets/TicketsContext";
import { filterValidContacts, isValidContact } from "../../utils/contactValidation";

const reducer = (state, action) => {
    if (action.type === "LOAD_CONTACTS") {
        const contacts = action.payload;

        // 🛡️ FILTRO CRÍTICO: Remover contatos fantasmas antes de adicionar ao estado
        const validContacts = filterValidContacts(contacts);

        const newContacts = [];

        validContacts.forEach((contact) => {
            const contactIndex = state.findIndex((c) => c.id === contact.id);
            if (contactIndex !== -1) {
                state[contactIndex] = contact;
            } else {
                newContacts.push(contact);
            }
        });

        return [...state, ...newContacts];
    }

    if (action.type === "UPDATE_CONTACTS") {
        const contact = action.payload;

        // 🛡️ FILTRO CRÍTICO: Validar contato antes de adicionar/atualizar
        if (!isValidContact(contact)) {
            console.warn('🚫 Contato fantasma bloqueado (UPDATE_CONTACTS):', {
                id: contact.id,
                name: contact.name,
                number: contact.number
            });
            return [...state]; // Retornar estado sem modificação
        }

        const contactIndex = state.findIndex((c) => c.id === contact.id);

        if (contactIndex !== -1) {
            state[contactIndex] = contact;
            return [...state];
        } else {
            return [contact, ...state];
        }
    }

    if (action.type === "DELETE_CONTACT") {
        const contactId = action.payload;

        const contactIndex = state.findIndex((c) => c.id === contactId);
        if (contactIndex !== -1) {
            state.splice(contactIndex, 1);
        }
        return [...state];
    }

    if (action.type === "RESET") {
        return [];
    }
};

const maskContactNumber = (number) => {
    const raw = String(number || "");
    const digits = raw.replace(/\D/g, "");
    if (!digits) return raw;
    if (digits.length <= 4) return "***";
    return `${digits.slice(0, 2)} *****-${digits.slice(-4)}`;
};

const useStyles = makeStyles((theme) => {
    const ds = theme.chatiaDS || {};
    const dsColors = ds.colors || {};
    const isDark = theme.palette.type === "dark" || theme.mode === "dark";
    return ({
    mainPaper: {
        flex: "1 1 auto",
        minHeight: 0,
        padding: 0,
        overflow: "hidden",
        borderRadius: ds.radius?.md ?? 12,
        background: dsColors.surfaceDefault || (isDark ? "#11141b" : "#f7f8fa"),
        border: `1px solid ${dsColors.border || (isDark ? "#202632" : "#e8e8e8")}`,
        boxShadow: isDark
            ? "0 2px 12px rgba(0,0,0,0.35)"
            : "0 2px 12px rgba(15,23,42,0.06)",
        display: "flex",
        flexDirection: "column",
    },
    tableScroll: {
        flex: 1,
        height: "100%",
        maxHeight: "100%",
        minHeight: 0,
        overflowX: "auto",
        overflowY: "auto",
        ...theme.scrollbarStyles,
    },
    cyberpunkCheckbox: {
        appearance: "none",
        WebkitAppearance: "none",
        MozAppearance: "none",
        width: 16,
        height: 16,
        border: `2px solid ${theme.palette.primary.main}`,
        borderRadius: 5,
        backgroundColor: "transparent",
        display: "inline-block",
        position: "relative",
        margin: 0,
        padding: 0,
        cursor: "pointer",
        flexShrink: 0,
        verticalAlign: "middle",
        "&::before": {
            content: '""',
            backgroundColor: theme.palette.primary.main,
            display: "block",
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%) scale(0)",
            width: 8,
            height: 8,
            borderRadius: 3,
            transition: "all 0.25s ease-in-out",
        },
        "&:checked::before": {
            transform: "translate(-50%, -50%) scale(1)",
        },
        "&:indeterminate::before": {
            transform: "translate(-50%, -50%) scale(1)",
            width: 10,
            height: 2,
            borderRadius: 1,
        },
    },
    excelTable: {
        width: "100%",
        borderCollapse: "separate",
        borderSpacing: 0,
        "& .MuiTableCell-root": {
            borderBottom: `1px solid ${dsColors.borderLight || (isDark ? "#1a1f29" : "#f0f0f0")}`,
            borderRight: "none",
            color: dsColors.textPrimary || (isDark ? "#e9edef" : "#111b21"),
            fontSize: 13.5,
            padding: "14px 16px",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
        },
        "& .MuiTableCell-paddingCheckbox": {
            padding: 0,
            textAlign: "center",
            verticalAlign: "middle",
            textOverflow: "clip",
            overflow: "visible",
            width: 44,
            "& > *": {
                verticalAlign: "middle",
            },
        },
        "& .MuiTableCell-head": {
            background: dsColors.surfaceDefault || (isDark ? "#11141b" : "#f7f8fa"),
            backgroundColor: dsColors.surfaceDefault || (isDark ? "#11141b" : "#f7f8fa"),
            color: dsColors.textSecondary || (isDark ? "#8696a0" : "#667781"),
            fontWeight: 700,
            fontSize: 11.5,
            textTransform: "uppercase",
            letterSpacing: 0.6,
            borderBottom: `1px solid ${dsColors.border || (isDark ? "#202632" : "#e8e8e8")}`,
            padding: "12px 16px",
            verticalAlign: "middle",
            zIndex: 3,
        },
        "& .MuiTableCell-head:first-child": {
            borderTopLeftRadius: ds.radius?.md ?? 12,
        },
        "& .MuiTableCell-head:last-child": {
            borderTopRightRadius: ds.radius?.md ?? 12,
        },
        "& tbody tr:last-child .MuiTableCell-root": {
            borderBottom: "none",
        },
    },
    zebraRow: {
        transition: "background-color 0.18s ease",
        "&:hover": {
            backgroundColor: dsColors.surfaceHover || (isDark ? "#171b24" : "#f3f4f6"),
        },
    },
    actionsCell: {
        whiteSpace: "nowrap",
        paddingRight: "16px !important",
        "& > *": {
            marginLeft: 4,
        },
        "& > *:first-child": {
            marginLeft: 0,
        },
    },
    iconActionBtn: {
        borderRadius: 10,
        padding: 6,
        transition: "all 0.18s ease",
        color: dsColors.textSecondary || (isDark ? "#8696a0" : "#667781"),
        "&:hover": {
            background: isDark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.06)",
            color: dsColors.textPrimary || (isDark ? "#e9edef" : "#111b21"),
        },
    },
    deleteActionBtn: {
        borderRadius: 10,
        padding: 6,
        transition: "all 0.18s ease",
        color: dsColors.danger || "#e53935",
        "&:hover": {
            background: dsColors.dangerBg || "rgba(229,57,53,0.10)",
            color: dsColors.dangerHover || "#c62828",
        },
    },
    helpIcon: {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        verticalAlign: "middle",
        color: isDark ? "#8696a0" : "#667781",
        cursor: "help",
        opacity: 0.6,
        marginLeft: 6,
        transition: "opacity 0.15s ease, color 0.15s ease",
        "&:hover": {
            opacity: 1,
            color: theme.palette.primary.main,
        },
    },
    helpTooltipTitle: {
        fontWeight: 600,
        marginBottom: 6,
        fontSize: 12,
    },
    helpTooltipSteps: {
        margin: 0,
        paddingLeft: 16,
        "& li": {
            marginBottom: 3,
        },
        "& li:last-child": {
            marginBottom: 0,
        },
    },
    headerCell: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        whiteSpace: "nowrap",
        gap: 2,
    },
    filterBtn: {
        padding: 2,
        marginLeft: 2,
        color: "inherit",
        opacity: 0.55,
        borderRadius: 8,
        "&:hover": {
            opacity: 1,
            background: isDark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.06)",
        },
    },
    filterBtnActive: {
        padding: 2,
        marginLeft: 2,
        borderRadius: 8,
        color: theme.palette.primary.main,
        background: isDark ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.06)",
        "&:hover": {
            background: isDark ? "rgba(255,255,255,0.14)" : "rgba(15,23,42,0.10)",
        },
    },
    filterTriggerBtn: {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        verticalAlign: "middle",
        gap: 6,
        height: 36,
        minWidth: 64,
        padding: "0 16px",
        borderRadius: 12,
        border: "none",
        boxSizing: "border-box",
        background: theme.palette.primary.main,
        color: theme.palette.primary.contrastText || "#fff",
        fontFamily: "inherit",
        fontSize: 13.5,
        fontWeight: 500,
        textTransform: "uppercase",
        letterSpacing: 0.4,
        lineHeight: 1.75,
        cursor: "pointer",
        boxShadow: "0 3px 1px -2px rgba(0,0,0,0.2),0 2px 2px 0 rgba(0,0,0,0.14),0 1px 5px 0 rgba(0,0,0,0.12)",
        transition: "background-color 250ms cubic-bezier(0.4,0,0.2,1), box-shadow 250ms cubic-bezier(0.4,0,0.2,1)",
        "&:hover": {
            background: theme.palette.primary.dark || theme.palette.primary.main,
            boxShadow: "0 2px 4px -1px rgba(0,0,0,0.2),0 4px 5px 0 rgba(0,0,0,0.14),0 1px 10px 0 rgba(0,0,0,0.12)",
        },
    },
    filterDrawerPaper: {
        width: 360,
        [theme.breakpoints.down("xs")]: { width: "85vw" },
        backgroundColor: isDark ? "#121212" : "#fff",
        backgroundImage: "none",
        boxShadow: "none",
        display: "flex",
        flexDirection: "column",
        borderTopRightRadius: 12,
        borderBottomRightRadius: 12,
        overflow: "hidden",
    },
    filterDrawerHeader: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "14px 16px",
        background: theme.palette.primary.main,
        color: theme.palette.primary.contrastText || "#fff",
        flex: "0 0 auto",
    },
    filterDrawerTitle: {
        fontWeight: 600,
        fontSize: 15,
        color: "inherit",
    },
    filterDrawerClose: {
        backgroundColor: "rgba(255, 255, 255, 0.12)",
        color: "#fff",
        transition: "all 0.2s ease",
        "&:hover": {
            backgroundColor: "rgba(255, 255, 255, 0.22)",
            color: "#fff",
        },
    },
    filterDrawerContent: {
        flex: "1 1 auto",
        overflowY: "auto",
        padding: theme.spacing(1, 0.5),
        ...theme.scrollbarStyles,
        "& .MuiOutlinedInput-root": { borderRadius: 12 },
        "& .MuiAutocomplete-inputRoot.MuiOutlinedInput-root": { borderRadius: "12px !important" },
    },
    filterSectionLabel: {
        fontSize: 12,
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: 0.5,
        color: isDark ? "#8696a0" : "#5a6a7a",
        padding: theme.spacing(1, 1.5, 0.5),
    },
    filterDrawerFooter: {
        flex: "0 0 auto",
        padding: "10px 14px",
        borderTop: `1px solid ${isDark ? "#202632" : "#e8e8e8"}`,
        display: "flex",
        justifyContent: "space-between",
        gap: 8,
    },
    filterMenu: {
        maxHeight: 300,
        minWidth: 160,
    },
    filterMenuItem: {
        fontSize: 12,
        minHeight: 30,
        padding: "4px 12px",
    },
    filterMenuItemActive: {
        fontSize: 12,
        minHeight: 30,
        padding: "4px 12px",
        fontWeight: 600,
        backgroundColor: theme.palette.action.selected,
    },
    truncatedCell: {
        maxWidth: 0,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    },
    // Mobile card styles
    mobileCard: {
        display: "flex",
        alignItems: "center",
        padding: "10px 12px",
        gap: 10,
        borderBottom: theme.palette.type === "dark" ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,0,0,0.06)",
        "&:active": {
            backgroundColor: theme.palette.action.selected,
        },
    },
    mobileCardAvatar: {
        width: 42,
        height: 42,
        flexShrink: 0,
    },
    mobileCardInfo: {
        flex: 1,
        minWidth: 0,
    },
    mobileCardName: {
        fontWeight: 600,
        fontSize: 14,
        color: theme.mode === "dark" ? "#e9edef" : "#111b21",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
    },
    mobileCardNumber: {
        fontSize: 12,
        color: theme.mode === "dark" ? "#8696a0" : "#667781",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
    },
    mobileCardTags: {
        display: "flex",
        gap: 3,
        flexWrap: "wrap",
        marginTop: 3,
        maxHeight: 22,
        overflow: "hidden",
    },
    mobileCardActions: {
        display: "flex",
        alignItems: "center",
        flexShrink: 0,
        gap: 2,
    },
    mobileHeader: {
        display: "flex",
        alignItems: "center",
        gap: 8,
        width: "100%",
        flexWrap: "wrap",
    },
    mobileHeaderRow: {
        display: "flex",
        alignItems: "center",
        gap: 8,
        width: "100%",
    },
    });
});

const Contacts = () => {
    const classes = useStyles();
    const history = useHistory();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const { user, socket } = useContext(AuthContext);

    const [loading, setLoading] = useState(false);
    const [pageNumber, setPageNumber] = useState(1);
    const { ticketSearchParam: searchParam, setTicketSearchParam: setSearchParam } = useSearch();
    const [contacts, dispatch] = useReducer(reducer, []);
    const [selectedContactId, setSelectedContactId] = useState(null);
    const [contactModalOpen, setContactModalOpen] = useState(false);

    const [importContactModalOpen, setImportContactModalOpen] = useState(false);
    const [deletingContact, setDeletingContact] = useState(null);
    const [ImportContacts, setImportContacts] = useState(null);
    
    const [blockingContact, setBlockingContact] = useState(null);
    const [unBlockingContact, setUnBlockingContact] = useState(null);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [exportContact, setExportContact] = useState(false);
    const [confirmChatsOpen, setConfirmChatsOpen] = useState(false);
    const [hasMore, setHasMore] = useState(false);
    const [totalFiltered, setTotalFiltered] = useState(0);
    const [newTicketModalOpen, setNewTicketModalOpen] = useState(false);
    const [contactTicket, setContactTicket] = useState({});
    const fileUploadRef = useRef(null);
    const [selectedTags, setSelectedTags] = useState([]);
    const { setCurrentTicket } = useContext(TicketsContext);

    const [importWhatsappId, setImportWhatsappId] = useState()

    // NOVOS ESTADOS PARA SELEÇÃO E DELEÇÃO EM MASSA
    const [selectedContactIds, setSelectedContactIds] = useState([]); // Array de IDs dos contatos selecionados
    const [isSelectAllChecked, setIsSelectAllChecked] = useState(false); // Estado para o checkbox "Selecionar Tudo"
    const [confirmDeleteManyOpen, setConfirmDeleteManyOpen] = useState(false); // Estado para o modal de confirmação de deleção em massa

    // Limpeza de contatos fantasmas (limitação Baileys)
    const [phantomCleanupOpen, setPhantomCleanupOpen] = useState(false);
    const [phantomCleanupLoading, setPhantomCleanupLoading] = useState(false);
    const [phantomCount, setPhantomCount] = useState(null);

    const [columnFilters, setColumnFilters] = useState({});
    const [filterAnchor, setFilterAnchor] = useState({ el: null, field: null });
    const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

    const { getAll: getAllSettings } = useCompanySettings();
    const [hideNum, setHideNum] = useState(false);

    useEffect(() => {
        async function fetchData() {
            const settingList = await getAllSettings(user.companyId);
            for (const [key, value] of Object.entries(settingList)) {
                if (key === "lgpdHideNumber") setHideNum(value === "enabled");
            }
        }
        fetchData();
    }, []);

    // Buscar opções de filtro do backend (valores distintos de cada coluna)
    const [columnOptions, setColumnOptions] = useState({ name: [], number: [], email: [], followUp: [], tags: [], products: [], attendant: [], payment: [] });

    useEffect(() => {
        const fetchFilterOptions = async () => {
            try {
                const { data } = await api.get("/contacts/filter-options");
                setColumnOptions(data);
            } catch (err) {
                // Fallback silencioso - opções ficarão vazias
            }
        };
        fetchFilterOptions();
    }, []);

    // Filtros de coluna agora são aplicados server-side, então filteredContacts = contacts
    const filteredContacts = contacts;

    const handleColumnFilterChange = (field) => (e) => {
        setColumnFilters(prev => ({ ...prev, [field]: e.target.value }));
    };

    const handleOpenFilter = (field) => (e) => {
        setFilterAnchor({ el: e.currentTarget, field });
    };

    const handleCloseFilter = () => {
        setFilterAnchor({ el: null, field: null });
    };

    const handleSelectFilter = (field, value) => {
        setColumnFilters(prev => {
            const next = { ...prev };
            if (value === "") {
                delete next[field];
            } else {
                next[field] = value;
            }
            return next;
        });
        handleCloseFilter();
    };

    const handleImportExcel = async () => {
        try {
            const formData = new FormData();
            formData.append("file", fileUploadRef.current.files[0]);
            await api.request({
                url: `/contacts/upload`,
                method: "POST",
                data: formData,
            });
            history.go(0);
        } catch (err) {
            toastError(err);
        }
    };

    useEffect(() => {
        dispatch({ type: "RESET" });
        setPageNumber(1);
        setSelectedContactIds([]); // Limpar seleção ao mudar filtro/pesquisa
        setIsSelectAllChecked(false); // Desmarcar "Selecionar Tudo"
    }, [searchParam, selectedTags, columnFilters]);

    useEffect(() => {
        setLoading(true);
        const delayDebounceFn = setTimeout(() => {
            const fetchContacts = async () => {
                try {
                    // Mapear columnFilters do frontend para o formato do backend
                    const activeFilters = Object.entries(columnFilters).filter(([, v]) => v !== "" && v !== undefined);
                    let backendFilters;
                    if (activeFilters.length > 0) {
                        backendFilters = {};
                        activeFilters.forEach(([field, val]) => {
                            switch (field) {
                                case "name": backendFilters.name = val; break;
                                case "number": backendFilters.number = val; break;
                                case "email": backendFilters.email = val; break;
                                case "followUp": backendFilters.followUp = val; break;
                                case "tags": backendFilters.tagName = val; break;
                                case "products": backendFilters.productName = val; break;
                                case "payment": backendFilters.productStatus = val; break;
                                case "attendant": backendFilters.attendantName = val; break;
                                default: break;
                            }
                        });
                    }

                    const params = {
                        searchParam,
                        pageNumber,
                        contactTag: JSON.stringify(selectedTags),
                    };
                    if (backendFilters) {
                        params.columnFilters = JSON.stringify(backendFilters);
                    }

                    const { data } = await api.get("/contacts/", { params });
                    dispatch({ type: "LOAD_CONTACTS", payload: data.contacts });
                    setHasMore(data.hasMore);
                    setTotalFiltered(data.count);
                    setLoading(false);

                    // Atualizar o estado do "Selecionar Tudo" baseado nos contatos carregados e selecionados
                    const allCurrentContactIds = data.contacts.map(c => c.id);
                    const newSelected = selectedContactIds.filter(id => allCurrentContactIds.includes(id));
                    setSelectedContactIds(newSelected); // Mantenha apenas os IDs que ainda estão na lista
                    setIsSelectAllChecked(newSelected.length === allCurrentContactIds.length && allCurrentContactIds.length > 0);

                } catch (err) {
                    toastError(err);
                }
            };
            fetchContacts();
        }, 500);
        return () => clearTimeout(delayDebounceFn);
    }, [searchParam, pageNumber, selectedTags, columnFilters]);

    useEffect(() => {
        const companyId = user.companyId;
        const onContactEvent = (data) => {
            if (data.action === "update" || data.action === "create") {
                const contact = data.contact;

                // 🛡️ FILTRO CRÍTICO #1: Validar se é um contato válido (não fantasma)
                if (!isValidContact(contact)) {
                    console.warn('🚫 Socket.IO: Contato fantasma bloqueado', {
                        id: contact.id,
                        name: contact.name,
                        number: contact.number,
                        reason: 'Número inválido'
                    });
                    return; // Bloquear contato fantasma
                }

                // 🛡️ FILTRO CRÍTICO #2: Validar filtro de busca
                if (searchParam && searchParam.trim() !== "") {
                    const contactName = contact.name?.toLowerCase() || "";
                    const contactNumber = contact.number?.toLowerCase() || "";
                    const contactEmail = contact.email?.toLowerCase() || "";

                    const matchesSearch =
                        contactName.includes(searchParam) ||
                        contactNumber.includes(searchParam) ||
                        contactEmail.includes(searchParam);

                    if (!matchesSearch) {
                        console.log("🚫 Socket.IO: Contato ignorado (não corresponde à busca)", {
                            contactId: contact.id,
                            contactName: contact.name,
                            searchParam
                        });
                        return;
                    }
                }

                // 🛡️ FILTRO CRÍTICO #3: Validar filtro de tags
                if (selectedTags.length > 0) {
                    const contactTagIds = contact.tags?.map(t => t.id) || [];
                    const hasSelectedTags = selectedTags.some(tagId =>
                        contactTagIds.includes(tagId)
                    );

                    if (!hasSelectedTags) {
                        console.log("🚫 Socket.IO: Contato ignorado (sem tags selecionadas)", {
                            contactId: contact.id,
                            contactName: contact.name,
                            contactTags: contactTagIds,
                            selectedTags
                        });
                        return;
                    }
                }

                // ✅ Contato passou em TODAS as validações
                console.log("✅ Socket.IO: Contato adicionado/atualizado", {
                    action: data.action,
                    contactId: contact.id,
                    contactName: contact.name
                });

                dispatch({ type: "UPDATE_CONTACTS", payload: contact });
            }

            if (data.action === "delete") {
                dispatch({ type: "DELETE_CONTACT", payload: +data.contactId });
                setSelectedContactIds((prevSelected) =>
                    prevSelected.filter((id) => id !== +data.contactId)
                );
            }
        };
        socket.on(`company-${companyId}-contact`, onContactEvent);

        return () => {
            socket.off(`company-${companyId}-contact`, onContactEvent);
        };
    }, [socket, searchParam, selectedTags]);

    const handleSelectTicket = (ticket) => {
        const code = uuidv4();
        const { id, uuid } = ticket;
        setCurrentTicket({ id, uuid, code });
    }

    const handleCloseOrOpenTicket = (ticket) => {
        setNewTicketModalOpen(false);
        if (ticket !== undefined && ticket.uuid !== undefined) {
            window.open(`/tickets/${ticket.uuid}`, "_blank");
        }
    };

    const handleSelectedTags = (selecteds) => {
        const tags = selecteds.map((t) => t.id);
        setSelectedTags(tags);
    };

    const handleSearch = (event) => {
        setSearchParam(event.target.value.toLowerCase());
    };

    const handleOpenContactModal = () => {
        setSelectedContactId(null);
        setContactModalOpen(true);
    };

    const handleCloseContactModal = () => {
        setSelectedContactId(null);
        setContactModalOpen(false);
    };

    const handleRefreshContacts = async () => {
        // Força atualização da lista de contatos
        try {
            const { data } = await api.get("/contacts/", {
                params: {
                    searchParam,
                    pageNumber: 1,
                    contactTag: JSON.stringify(selectedTags)
                },
            });
            dispatch({ type: "RESET" });
            dispatch({ type: "LOAD_CONTACTS", payload: data.contacts });
            setHasMore(data.hasMore);
        } catch (err) {
            toastError(err);
        }
    };

    const hadleEditContact = (contactId) => {
        setSelectedContactId(contactId);
        setContactModalOpen(true);
    };

    const handleDeleteContact = async (contactId) => {
        try {
            await api.delete(`/contacts/${contactId}`);
            toast.success(i18n.t("contacts.toasts.deleted"));
        } catch (err) {
            toastError(err);
        }
        setDeletingContact(null);
    };

    // Detecta contatos fantasmas com regras mais agressivas que isValidContact
    // (esta versão é exclusiva da limpeza — não afeta a renderização normal)
    const isPhantomForCleanup = (c) => {
        if (!c || c.isGroup) return false;

        // 1. Regras já existentes em isValidContact
        if (!isValidContact(c)) return true;

        const number = (c.number || "").replace(/\D/g, "");
        const name = (c.name || "").trim();
        const nameDigits = name.replace(/\D/g, "");

        // 2. Nome IGUAL ao número (auto-criado pelo Baileys quando não resolve o nome)
        if (number && nameDigits && nameDigits === number) return true;

        // 3. Nome ausente OU vazio quando tem número
        if (number && (!name || name.length < 2)) return true;

        // 4. JIDs de broadcast/grupo do WhatsApp (120363xxx…)
        if (/^120363\d+/.test(number)) return true;

        // 5. Números com mais de 15 dígitos (E.164 máximo)
        if (number.length > 15) return true;

        // 6. Source auto_created sem estar na agenda
        if (c.source === "auto_created" && c.isInAgenda === false) return true;

        return false;
    };

    // Identifica e remove contatos fantasmas (criados automaticamente pelo Baileys)
    const handleScanPhantoms = async () => {
        setPhantomCleanupLoading(true);
        try {
            // Busca um lote grande pra escanear toda a base
            const { data } = await api.get("/contacts", { params: { searchParam: "", pageNumber: 1, pageSize: 100000 } });
            const allContacts = Array.isArray(data?.contacts) ? data.contacts : [];
            const phantoms = allContacts.filter(isPhantomForCleanup);
            setPhantomCount(phantoms.length);
            setPhantomCleanupOpen(true);
        } catch (err) {
            toastError(err);
        } finally {
            setPhantomCleanupLoading(false);
        }
    };

    const handleConfirmPhantomCleanup = async () => {
        setPhantomCleanupLoading(true);
        try {
            const { data } = await api.get("/contacts", { params: { searchParam: "", pageNumber: 1, pageSize: 100000 } });
            const allContacts = Array.isArray(data?.contacts) ? data.contacts : [];
            const phantoms = allContacts.filter(isPhantomForCleanup);

            // Sem fantasmas → nada a fazer (não mexe na lista)
            if (phantoms.length === 0) {
                toast.info(i18n.t("contacts.phantomCleanup.noneFound") || "Nenhum contato fantasma encontrado.");
                setPhantomCleanupOpen(false);
                setPhantomCount(null);
                return;
            }

            let deleted = 0;
            const batchSize = 5;
            for (let i = 0; i < phantoms.length; i += batchSize) {
                const batch = phantoms.slice(i, i + batchSize);
                await Promise.allSettled(batch.map((p) => api.delete(`/contacts/${p.id}`).then(() => { deleted++; })));
            }

            toast.success(`${deleted} ${i18n.t("contacts.phantomCleanup.deletedSuccess") || "contatos fantasmas removidos"}`);
            setPhantomCleanupOpen(false);
            setPhantomCount(null);

            // Recarrega a lista de forma segura — fetch + dispatch atômico, sem deixar a tela vazia
            try {
                const { data: fresh } = await api.get("/contacts/", {
                    params: { searchParam, pageNumber: 1, contactTag: JSON.stringify(selectedTags) },
                });
                dispatch({ type: "RESET" });
                dispatch({ type: "LOAD_CONTACTS", payload: fresh.contacts });
                setHasMore(fresh.hasMore);
                setPageNumber(1);
            } catch (refreshErr) {
                // Se o reload falhar, NÃO esvazia a tela — mantém o que tinha
                toastError(refreshErr);
            }
        } catch (err) {
            toastError(err);
        } finally {
            setPhantomCleanupLoading(false);
        }
    };

    // NOVA FUNÇÃO: SELECIONAR UM CONTATO INDIVIDUALMENTE
    const handleToggleSelectContact = (contactId) => (event) => {
        if (event.target.checked) {
            setSelectedContactIds((prevSelected) => [...prevSelected, contactId]);
        } else {
            setSelectedContactIds((prevSelected) => prevSelected.filter((id) => id !== contactId));
            setIsSelectAllChecked(false); // Se um individual é desmarcado, "Selecionar Tudo" deve ser desmarcado
        }
    };

    // NOVA FUNÇÃO: SELECIONAR/DESSELECIONAR TODOS OS CONTATOS
    const handleSelectAllContacts = (event) => {
        const checked = event.target.checked;
        setIsSelectAllChecked(checked);

        if (checked) {
            // Seleciona todos os IDs dos contatos filtrados visíveis
            const allContactIds = filteredContacts.map((contact) => contact.id);
            setSelectedContactIds(allContactIds);
        } else {
            setSelectedContactIds([]);
        }
    };

    // NOVA FUNÇÃO: DELETAR CONTATOS SELECIONADOS EM MASSA
    const handleDeleteSelectedContacts = async () => {
        try {
            setLoading(true);
            await api.delete("/contacts/batch-delete", {
                data: { contactIds: selectedContactIds }
            });
            toast.success(i18n.t("contacts.bulkActions.deleteSuccess"));
            setSelectedContactIds([]);
            setIsSelectAllChecked(false);
            setConfirmDeleteManyOpen(false);

            // Reload atômico — busca a nova lista PRIMEIRO, depois substitui o state
            // (evita o "flash de tela vazia" que parecia ter apagado tudo)
            try {
                const { data: fresh } = await api.get("/contacts/", {
                    params: { searchParam, pageNumber: 1, contactTag: JSON.stringify(selectedTags) },
                });
                dispatch({ type: "RESET" });
                dispatch({ type: "LOAD_CONTACTS", payload: fresh.contacts });
                setHasMore(fresh.hasMore);
                setPageNumber(1);
            } catch (refreshErr) {
                // Se o reload falhar, ao menos remove os deletados do state local
                selectedContactIds.forEach((id) => {
                    dispatch({ type: "DELETE_CONTACT", payload: id });
                });
            }
        } catch (err) {
            toastError(err);
        } finally {
            setLoading(false);
        }
    };


    const handleBlockContact = async (contactId) => {
        try {
            await api.put(`/contacts/block/${contactId}`, { active: false });
            toast.success(i18n.t("contacts.bulkActions.blockContact"));
        } catch (err) {
            toastError(err);
        }
        setDeletingContact(null);
        setSearchParam("");
        setPageNumber(1);
        setBlockingContact(null);
    };

    const handleUnBlockContact = async (contactId) => {
        try {
            await api.put(`/contacts/block/${contactId}`, { active: true });
            toast.success(i18n.t("contacts.bulkActions.unblockContact"));
        } catch (err) {
            toastError(err);
        }
        setDeletingContact(null);
        setSearchParam("");
        setPageNumber(1);
        setUnBlockingContact(null);
    };

    const onSave = (whatsappId) => {
        setImportWhatsappId(whatsappId)
    }

    const handleimportContact = async () => {
        setImportContactModalOpen(false)

        try {
            await api.post("/contacts/import", { whatsappId: importWhatsappId });
            history.go(0);
            setImportContactModalOpen(false);
        } catch (err) {
            toastError(err);
            setImportContactModalOpen(false);
        }
    };

    const handleimportChats = async () => {
        console.log("handleimportChats")
        try {
            await api.post("/contacts/import/chats");
            history.go(0);
        } catch (err) {
            toastError(err);
        }
    };

    const loadMore = () => {
        setPageNumber((prevState) => prevState + 1);
    };

    const handleScroll = (e) => {
        if (!hasMore || loading) return;
        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
        if (scrollHeight - (scrollTop + 100) < clientHeight) {
            loadMore();
        }
    };

    return (
        <MainContainer className={classes.mainContainer}>
            <Drawer
                anchor="left"
                open={filterDrawerOpen}
                onClose={() => setFilterDrawerOpen(false)}
                classes={{ paper: classes.filterDrawerPaper }}
            >
                <div className={classes.filterDrawerHeader}>
                    <Typography className={classes.filterDrawerTitle}>
                        {i18n.t("contacts.filters.title") || "Filtros"}
                    </Typography>
                    <IconButton
                        size="small"
                        aria-label="close-filter"
                        className={classes.filterDrawerClose}
                        onClick={() => setFilterDrawerOpen(false)}
                    >
                        <XIcon size={18} />
                    </IconButton>
                </div>
                <div className={classes.filterDrawerContent}>
                    <Typography className={classes.filterSectionLabel}>
                        {i18n.t("contacts.filters.tagsLabel") || "Tags"}
                    </Typography>
                    <div style={{ padding: "0 12px 8px" }}>
                        <TagsFilter onFiltered={handleSelectedTags} />
                    </div>
                    {[
                        { field: "name", label: i18n.t("contacts.table.name") },
                        { field: "number", label: i18n.t("contacts.table.whatsapp") },
                        { field: "email", label: i18n.t("contacts.table.email") },
                        { field: "followUp", label: i18n.t("contacts.table.followUp") },
                        { field: "products", label: i18n.t("contacts.table.products") },
                        { field: "payment", label: i18n.t("contacts.table.payment") },
                        { field: "attendant", label: i18n.t("contacts.table.attendant") },
                    ].map(({ field, label }) => (
                        <div key={field}>
                            <Typography className={classes.filterSectionLabel}>{label}</Typography>
                            <div style={{ padding: "0 12px 8px" }}>
                                <Autocomplete
                                    options={columnOptions[field] || []}
                                    value={columnFilters[field] || null}
                                    onChange={(_, v) => handleSelectFilter(field, v || "")}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            variant="outlined"
                                            size="small"
                                            placeholder={i18n.t("contacts.filters.any") || "Qualquer"}
                                        />
                                    )}
                                    clearOnEscape
                                />
                            </div>
                        </div>
                    ))}
                </div>
                <div className={classes.filterDrawerFooter}>
                    <Button
                        size="small"
                        onClick={() => setColumnFilters({})}
                        disabled={Object.values(columnFilters).filter((v) => v !== "" && v !== undefined && v !== null).length === 0}
                    >
                        {i18n.t("contacts.filters.clearAll") || "Limpar filtros"}
                    </Button>
                    <Button
                        size="small"
                        variant="contained"
                        color="primary"
                        onClick={() => setFilterDrawerOpen(false)}
                    >
                        {i18n.t("contacts.filters.apply") || "Aplicar"}
                    </Button>
                </div>
            </Drawer>
            <NewTicketModal
                modalOpen={newTicketModalOpen}
                initialContact={contactTicket}
                onClose={(ticket) => {
                    handleCloseOrOpenTicket(ticket);
                }}
            />
            <ContactModal
                open={contactModalOpen}
                onClose={handleCloseContactModal}
                aria-labelledby="form-dialog-title"
                contactId={selectedContactId}
                onSaveSuccess={handleRefreshContacts}
            ></ContactModal>
            
            <ConfirmationModal
                title={
                    deletingContact
                        ? `${i18n.t(
                            "contacts.confirmationModal.deleteTitle"
                        )} ${deletingContact.name}?`
                        : blockingContact
                            ? i18n.t("contacts.confirmationModal.blockContact")
                            : unBlockingContact
                                ? i18n.t("contacts.confirmationModal.unblockContact")
                                : ImportContacts
                                    ? `${i18n.t("contacts.confirmationModal.importTitlte")}`
                                    : `${i18n.t("contactListItems.confirmationModal.importTitlte")}`
                }
                onSave={onSave}
                isCellPhone={ImportContacts}
                open={confirmOpen}
                onClose={setConfirmOpen}
                onConfirm={(e) =>
                    deletingContact
                        ? handleDeleteContact(deletingContact.id)
                        : blockingContact
                            ? handleBlockContact(blockingContact.id)
                            : unBlockingContact
                                ? handleUnBlockContact(unBlockingContact.id)
                                : ImportContacts
                                    ? handleimportContact()
                                    : handleImportExcel()
                }
            >
                {exportContact
                    ?
                    `${i18n.t("contacts.confirmationModal.exportContact")}`
                    : deletingContact
                        ? `${i18n.t("contacts.confirmationModal.deleteMessage")}`
                        : blockingContact
                            ? `${i18n.t("contacts.confirmationModal.blockContact")}`
                            : unBlockingContact
                                ? `${i18n.t("contacts.confirmationModal.unblockContact")}`
                                : ImportContacts
                                    ? i18n.t("contacts.bulkActions.selectConnectionToImport")
                                    : `${i18n.t(
                                        "contactListItems.confirmationModal.importMessage"
                                    )}`}
            </ConfirmationModal>

            {/* NOVO MODAL DE CONFIRMAÇÃO PARA DELEÇÃO EM MASSA */}
            <ConfirmationModal
                title={i18n.t("contacts.bulkActions.deleteConfirmTitle", { count: selectedContactIds.length })}
                open={confirmDeleteManyOpen}
                onClose={() => setConfirmDeleteManyOpen(false)}
                onConfirm={handleDeleteSelectedContacts}
            >
                {i18n.t("contacts.bulkActions.deleteConfirmMessage")}
            </ConfirmationModal>

            {/* CONFIRMAÇÃO DE LIMPEZA DE FANTASMAS */}
            <ConfirmationModal
                title={i18n.t("contacts.phantomCleanup.confirmTitle") || "Limpar contatos fantasmas"}
                open={phantomCleanupOpen}
                onClose={() => setPhantomCleanupOpen(false)}
                onConfirm={handleConfirmPhantomCleanup}
            >
                {phantomCount === 0
                    ? (i18n.t("contacts.phantomCleanup.noneFound") || "Nenhum contato fantasma encontrado na sua base. Tudo limpo!")
                    : (
                        <>
                            {(i18n.t("contacts.phantomCleanup.confirmMessage") || "Foram encontrados {{count}} contatos fantasmas. Eles serão removidos permanentemente do banco de dados. Deseja continuar?")
                                .replace("{{count}}", phantomCount ?? 0)}
                        </>
                    )}
            </ConfirmationModal>


            <ConfirmationModal
                title={i18n.t("contacts.confirmationModal.importChat")}
                open={confirmChatsOpen}
                onClose={setConfirmChatsOpen}
                onConfirm={(e) => handleimportChats()}
            >
                {i18n.t("contacts.confirmationModal.wantImport")}
            </ConfirmationModal>

            <HubPageHeader
                icon={<ContactPhone size={20} strokeWidth={1.8} />}
                title={i18n.t("contacts.title")}
                subtitle={`${contacts.length}${totalFiltered > contacts.length ? `/${totalFiltered}` : ""} ${i18n.t("contacts.subtitleCount") || "contatos"}`}
                action={isMobile ? (
                    <div className={classes.mobileHeader}>
                        <div className={classes.mobileHeaderRow}>
                            <TextField
                                style={{ flex: 1 }}
                                placeholder={i18n.t("contacts.searchPlaceholder")}
                                type="search"
                                size="small"
                                variant="outlined"
                                value={searchParam}
                                onChange={handleSearch}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon size={18} color="gray" />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleOpenContactModal}
                                style={{ whiteSpace: "nowrap", minWidth: "auto", padding: "8px 16px" }}
                            >
                                + Novo
                            </Button>
                        </div>
                        <div className={classes.mobileHeaderRow}>
                            <TagsFilter onFiltered={handleSelectedTags} />
                            <PopupState variant="popover" popupId="demo-popup-menu">
                                {(popupState) => (
                                    <React.Fragment>
                                        <Button
                                            variant="outlined"
                                            color="primary"
                                            size="small"
                                            {...bindTrigger(popupState)}
                                        >
                                            Importar <ArrowDropDown size={16} />
                                        </Button>
                                        <Menu {...bindMenu(popupState)}>
                                            <MenuItem onClick={() => { setConfirmOpen(true); setImportContacts(true); popupState.close(); }}>
                                                <ContactPhone size={18} style={{ marginRight: 10, color: theme.palette.primary.main }} />
                                                {i18n.t("contacts.menu.importYourPhone")}
                                            </MenuItem>
                                            <MenuItem onClick={() => { setImportContactModalOpen(true); popupState.close(); }}>
                                                <Backup size={18} style={{ marginRight: 10, color: theme.palette.primary.main }} />
                                                {i18n.t("contacts.menu.importToExcel")}
                                            </MenuItem>
                                        </Menu>
                                    </React.Fragment>
                                )}
                            </PopupState>
                        </div>
                    </div>
                ) : (
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 8, flexWrap: "wrap" }}>
                        <button
                            type="button"
                            className={classes.filterTriggerBtn}
                            onClick={() => setFilterDrawerOpen(true)}
                        >
                            <FilterIcon size={16} strokeWidth={1.8} />
                            <span>{i18n.t("contacts.filters.title") || "Filtros"}</span>
                            {Object.values(columnFilters).filter((v) => v !== "" && v !== undefined && v !== null).length > 0 && (
                                <span style={{
                                    marginLeft: 4, padding: "1px 7px", borderRadius: 999,
                                    background: "rgba(255,255,255,0.22)", fontSize: 11, fontWeight: 700,
                                }}>
                                    {Object.values(columnFilters).filter((v) => v !== "" && v !== undefined && v !== null).length}
                                </span>
                            )}
                        </button>
                        <PopupState variant="popover" popupId="demo-popup-menu">
                            {(popupState) => (
                                <React.Fragment>
                                    <Button variant="contained" color="primary" {...bindTrigger(popupState)} style={{ borderRadius: 12 }} endIcon={<ArrowDropDown size={16} />}>
                                        {i18n.t("contacts.menu.importExport")}
                                    </Button>
                                    <Menu
                                        {...bindMenu(popupState)}
                                        getContentAnchorEl={null}
                                        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                                        transformOrigin={{ vertical: "top", horizontal: "right" }}
                                        PaperProps={{
                                            style: {
                                                marginTop: 6,
                                                minWidth: 240,
                                                borderRadius: 12,
                                                background: theme.mode === "dark" ? "#121212" : "#ffffff",
                                                border: `1px solid ${theme.mode === "dark" ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)"}`,
                                                boxShadow: theme.mode === "dark"
                                                    ? "0 8px 24px rgba(0,0,0,0.45)"
                                                    : "0 8px 24px rgba(15,23,42,0.12)",
                                                overflow: "hidden",
                                            },
                                        }}
                                    >
                                        <MenuItem dense onClick={() => { setConfirmOpen(true); setImportContacts(true); popupState.close(); }} style={{ gap: 10, fontSize: 13.5, padding: "10px 14px" }}>
                                            <ContactPhone size={18} color={theme.palette.primary.main} />
                                            {i18n.t("contacts.menu.importYourPhone")}
                                        </MenuItem>
                                        <MenuItem dense onClick={() => { setImportContactModalOpen(true); popupState.close(); }} style={{ gap: 10, fontSize: 13.5, padding: "10px 14px" }}>
                                            <Backup size={18} color={theme.palette.primary.main} />
                                            {i18n.t("contacts.menu.importToExcel")}
                                        </MenuItem>
                                    </Menu>
                                </React.Fragment>
                            )}
                        </PopupState>
                        <Button
                            variant="outlined"
                            onClick={handleScanPhantoms}
                            disabled={phantomCleanupLoading}
                            startIcon={<Sparkles size={16} strokeWidth={1.8} />}
                            endIcon={
                                <AppTooltip
                                    placement="bottom"
                                    title={
                                        <div>
                                            <div className={classes.helpTooltipTitle}>
                                                {i18n.t("contacts.phantomCleanup.helpTitle") || "Por que aparecem contatos fantasmas?"}
                                            </div>
                                            <div style={{ fontSize: 12, marginBottom: 6 }}>
                                                {i18n.t("contacts.phantomCleanup.helpIntro")}
                                            </div>
                                            <ol className={classes.helpTooltipSteps}>
                                                <li>{i18n.t("contacts.phantomCleanup.helpReason1")}</li>
                                                <li>{i18n.t("contacts.phantomCleanup.helpReason2")}</li>
                                                <li>{i18n.t("contacts.phantomCleanup.helpFixBody")}</li>
                                            </ol>
                                        </div>
                                    }
                                >
                                    <span
                                        className={classes.helpIcon}
                                        onClick={(e) => e.stopPropagation()}
                                        style={{ marginLeft: 0 }}
                                    >
                                        <HelpCircle size={14} strokeWidth={2} />
                                    </span>
                                </AppTooltip>
                            }
                            style={{
                                borderRadius: 12,
                                textTransform: "none",
                                fontWeight: 600,
                                borderColor: theme.mode === "dark" ? "rgba(255,255,255,0.15)" : "rgba(15,23,42,0.15)",
                                color: theme.mode === "dark" ? "#e9edef" : "#0f172a",
                            }}
                        >
                            {i18n.t("contacts.phantomCleanup.button") || "Limpar fantasmas"}
                        </Button>
                        <Button variant="contained" color="primary" onClick={handleOpenContactModal} style={{ borderRadius: 12 }}>
                            {i18n.t("contacts.buttons.add")}
                        </Button>
                    </div>
                )}
            />

            {importContactModalOpen && (
                <ContactImportWpModal
                    isOpen={importContactModalOpen}
                    handleClose={() => setImportContactModalOpen(false)}
                    selectedTags={selectedTags}
                    hideNum={hideNum}
                    userProfile={user.profile}
                />
            )}
            <input
                style={{ display: "none" }}
                id="upload"
                name="file"
                type="file"
                accept=".xls,.xlsx"
                onChange={() => {
                    setConfirmOpen(true);
                }}
                ref={fileUploadRef}
            />
            {/* Menu popup de filtro por coluna (fora da Table) */}
            <Menu
                anchorEl={filterAnchor.el}
                open={Boolean(filterAnchor.el)}
                onClose={handleCloseFilter}
                PaperProps={{ className: classes.filterMenu }}
            >
                <MenuItem
                    className={!columnFilters[filterAnchor.field] ? classes.filterMenuItemActive : classes.filterMenuItem}
                    onClick={() => handleSelectFilter(filterAnchor.field, "")}
                >
                    (Todos)
                </MenuItem>
                {(columnOptions[filterAnchor.field] || []).map(opt => (
                    <MenuItem
                        key={opt}
                        className={columnFilters[filterAnchor.field] === opt ? classes.filterMenuItemActive : classes.filterMenuItem}
                        onClick={() => handleSelectFilter(filterAnchor.field, opt)}
                    >
                        {opt}
                    </MenuItem>
                ))}
            </Menu>
            <Paper
                className={classes.mainPaper}
                variant="outlined"
            >
                {isMobile ? (
                    /* ===== MOBILE: Cards ===== */
                    <div className={classes.tableScroll} onScroll={handleScroll}>
                        {filteredContacts.map((contact) => (
                            <div key={contact.id} className={classes.mobileCard}>
                                <input
                                    type="checkbox"
                                    className={classes.cyberpunkCheckbox}
                                    checked={selectedContactIds.includes(contact.id)}
                                    onChange={handleToggleSelectContact(contact.id)}
                                />
                                <Avatar src={`${contact?.urlPicture}`} className={classes.mobileCardAvatar} />
                                <div className={classes.mobileCardInfo} onClick={() => hadleEditContact(contact.id)}>
                                    <Typography className={classes.mobileCardName}>
                                        {contact.name}
                                    </Typography>
                                    <Typography className={classes.mobileCardNumber}>
                                        {maskContactNumber(contact.number)}
                                    </Typography>
                                    {contact.tags?.length > 0 && (
                                        <div className={classes.mobileCardTags}>
                                            {contact.tags.slice(0, 3).map((tag) => (
                                                <Chip
                                                    key={tag.id}
                                                    label={tag.name}
                                                    size="small"
                                                    style={{
                                                        backgroundColor: tag.color || "#ccc",
                                                        color: "#fff",
                                                        fontSize: 10,
                                                        height: 18,
                                                        maxWidth: 70,
                                                    }}
                                                />
                                            ))}
                                            {contact.tags.length > 3 && (
                                                <Chip label={`+${contact.tags.length - 3}`} size="small" style={{ fontSize: 10, height: 18 }} />
                                            )}
                                        </div>
                                    )}
                                </div>
                                <div className={classes.mobileCardActions}>
                                    <AppTooltip titleKey="contacts.buttons.startChat">
                                        <IconButton
                                            size="small"
                                            className={classes.iconActionBtn}
                                            disabled={!contact.active}
                                            onClick={() => { setContactTicket(contact); setNewTicketModalOpen(true); }}
                                        >
                                            <WhatsAppIcon size={18} strokeWidth={2} />
                                        </IconButton>
                                    </AppTooltip>
                                    {!contact.active && (
                                        <BlockIcon size={16} color="#e53935" />
                                    )}
                                </div>
                            </div>
                        ))}
                        {loading && <TableRowSkeleton avatar columns={2} />}
                    </div>
                ) : (
                /* ===== DESKTOP: Tabela ===== */
                <div className={classes.tableScroll} onScroll={handleScroll} style={{ position: "relative" }}>
                {selectedContactIds.length > 0 && (
                    <AppTooltip title={i18n.t("contacts.bulkActions.deleteSelected", { count: selectedContactIds.length })} arrow>
                        <button
                            type="button"
                            onClick={() => setConfirmDeleteManyOpen(true)}
                            disabled={loading}
                            style={{
                                position: "absolute",
                                top: 11,
                                left: 56,
                                background: "linear-gradient(135deg, #e53935 0%, #c62828 100%)",
                                color: "#fff",
                                width: 30,
                                height: 30,
                                borderRadius: 10,
                                border: "none",
                                boxShadow: "0 4px 12px rgba(229,57,53,0.35)",
                                zIndex: 1100,
                                padding: 0,
                                cursor: loading ? "not-allowed" : "pointer",
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                transition: "transform 0.2s ease, box-shadow 0.2s ease",
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.transform = "scale(1.08)";
                                e.currentTarget.style.boxShadow = "0 6px 18px rgba(229,57,53,0.45)";
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.transform = "scale(1)";
                                e.currentTarget.style.boxShadow = "0 4px 12px rgba(229,57,53,0.35)";
                            }}
                        >
                            <DeleteOutlineIcon size={14} strokeWidth={2.2} />
                            <span style={{
                                position: "absolute",
                                top: -6,
                                right: -6,
                                background: "#fff",
                                color: "#e53935",
                                fontSize: 10,
                                fontWeight: 800,
                                minWidth: 16,
                                height: 16,
                                borderRadius: 8,
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                padding: "0 4px",
                                boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                                lineHeight: 1,
                            }}>
                                {selectedContactIds.length}
                            </span>
                        </button>
                    </AppTooltip>
                )}
                <Table size="small" stickyHeader className={classes.excelTable} style={{ minWidth: 1770, tableLayout: "fixed", width: "100%" }}>
                    <colgroup>
                        <col style={{ width: 44 }} />
                        <col style={{ width: 56 }} />
                        <col style={{ width: 220 }} />
                        <col style={{ width: 180 }} />
                        <col style={{ width: 240 }} />
                        <col style={{ width: 160 }} />
                        <col style={{ width: 160 }} />
                        <col style={{ width: 140 }} />
                        <col style={{ width: 100 }} />
                        <col style={{ width: 140 }} />
                        <col style={{ width: 130 }} />
                        <col style={{ width: 200 }} />
                    </colgroup>
                    <TableHead>
                        <TableRow>
                            <TableCell padding="checkbox">
                                <input
                                    type="checkbox"
                                    className={classes.cyberpunkCheckbox}
                                    checked={isSelectAllChecked}
                                    ref={(el) => {
                                        if (el) {
                                            const someSelected = selectedContactIds.length > 0 && !isSelectAllChecked;
                                            el.indeterminate = someSelected;
                                        }
                                    }}
                                    onChange={handleSelectAllContacts}
                                    aria-label={i18n.t("contacts.table.selectAll")}
                                />
                            </TableCell>
                            <TableCell />
                            {[
                                { field: "name", label: i18n.t("contacts.table.name") },
                                { field: "number", label: i18n.t("contacts.table.whatsapp") },
                                { field: "email", label: i18n.t("contacts.table.email") },
                                { field: "followUp", label: i18n.t("contacts.table.followUp") },
                                { field: "tags", label: i18n.t("contacts.table.tags") },
                                { field: "products", label: i18n.t("contacts.table.products") },
                                { field: "payment", label: i18n.t("contacts.table.payment") },
                                { field: "attendant", label: i18n.t("contacts.table.attendant") },
                            ].map(({ field, label }) => (
                                <TableCell key={field} align="center">
                                    {label}
                                </TableCell>
                            ))}
                            <TableCell align="center">{i18n.t("contacts.table.status")}</TableCell>
                            <TableCell align="right" className={classes.actionsCell}>{i18n.t("contacts.table.actions")}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <>
                            {filteredContacts.map((contact) => (
                                <TableRow key={contact.id} className={classes.zebraRow}>
                                    <TableCell padding="checkbox">
                                        <input
                                            type="checkbox"
                                            className={classes.cyberpunkCheckbox}
                                            checked={selectedContactIds.includes(contact.id)}
                                            onChange={handleToggleSelectContact(contact.id)}
                                            aria-label={i18n.t("contacts.table.selectContact", { name: contact.name })}
                                        />
                                    </TableCell>
                                    <TableCell style={{ paddingRight: 0, overflow: "visible" }}>
                                        <Avatar src={`${contact?.urlPicture}`} style={{ width: 30, height: 30 }} />
                                    </TableCell>
                                    <TableCell className={classes.truncatedCell}>
                                        <AppTooltip title={contact.name || ""} arrow>
                                            <span>{contact.name}</span>
                                        </AppTooltip>
                                    </TableCell>
                                    <TableCell align="center" className={classes.truncatedCell}>
                                        <Box display="flex" alignItems="center" justifyContent="center" style={{ gap: '4px' }}>
                                            {contact.isGroup && (
                                                <GroupIcon size={14} color="#25D366" />
                                            )}
                                            {maskContactNumber(contact.number)}
                                        </Box>
                                    </TableCell>
                                    <TableCell align="center" className={classes.truncatedCell}>
                                        <AppTooltip title={contact.email || ""} arrow>
                                            <span>{contact.email}</span>
                                        </AppTooltip>
                                    </TableCell>
                                    <TableCell align="center" className={classes.truncatedCell}>
                                        <AppTooltip title={contact.followUp || ""} arrow>
                                            <span>{contact.followUp}</span>
                                        </AppTooltip>
                                    </TableCell>
                                    <TableCell align="center">
                                        <AppTooltip title={contact.tags?.map(t => t.name).join(", ") || ""} arrow>
                                            <div style={{ display: "flex", flexWrap: "wrap", gap: 2, justifyContent: "center", overflow: "hidden", maxHeight: 48 }}>
                                                {contact.tags?.slice(0, 3).map((tag) => (
                                                    <Chip
                                                        key={tag.id}
                                                        label={tag.name}
                                                        size="small"
                                                        style={{
                                                            backgroundColor: tag.color || "#ccc",
                                                            color: "#fff",
                                                            fontSize: 10,
                                                            height: 20,
                                                            maxWidth: 80,
                                                        }}
                                                    />
                                                ))}
                                                {contact.tags?.length > 3 && (
                                                    <Chip label={`+${contact.tags.length - 3}`} size="small" style={{ fontSize: 10, height: 20 }} />
                                                )}
                                            </div>
                                        </AppTooltip>
                                    </TableCell>
                                    <TableCell align="center">
                                        <AppTooltip title={contact.products?.map(p => p.name).join(", ") || ""} arrow>
                                            <div style={{ display: "flex", flexDirection: "column", gap: 1, alignItems: "center", overflow: "hidden", maxHeight: 48 }}>
                                                {contact.products?.slice(0, 2).map((p) => (
                                                    <Chip
                                                        key={p.id}
                                                        label={p.name}
                                                        size="small"
                                                        variant="outlined"
                                                        style={{ fontSize: 10, height: 20, maxWidth: 100 }}
                                                    />
                                                ))}
                                                {contact.products?.length > 2 && (
                                                    <Chip label={`+${contact.products.length - 2}`} size="small" style={{ fontSize: 10, height: 20 }} />
                                                )}
                                            </div>
                                        </AppTooltip>
                                    </TableCell>
                                    <TableCell align="center">
                                        <div style={{ display: "flex", flexDirection: "column", gap: 1, alignItems: "center", overflow: "hidden", maxHeight: 48 }}>
                                            {contact.products?.slice(0, 2).map((p) => {
                                                const s = (p.status || "").toLowerCase();
                                                const bg =
                                                    (s.includes("complet") || s.includes("comprado") || s.includes("aprovado")) ? "#4caf50" :
                                                    (s.includes("cancel") || s.includes("chargeback")) ? "#ffffff" :
                                                    (s.includes("reembols") || s.includes("refund")) ? "#ff9800" :
                                                    (s.includes("pend") || s.includes("aguardando")) ? "#2196f3" :
                                                    (s.includes("expirado") || s.includes("carrinho")) ? "#9c27b0" :
                                                    "#9e9e9e";
                                                return (
                                                    <Chip
                                                        key={`pay-${p.id}`}
                                                        label={p.status}
                                                        size="small"
                                                        style={{ fontSize: 10, height: 20, backgroundColor: bg, color: "#fff" }}
                                                    />
                                                );
                                            })}
                                            {contact.products?.length > 2 && (
                                                <Chip label={`+${contact.products.length - 2}`} size="small" style={{ fontSize: 10, height: 20 }} />
                                            )}
                                        </div>
                                    </TableCell>
                                    <TableCell align="center" className={classes.truncatedCell}>
                                        <AppTooltip title={(() => { const t = contact.tickets?.find(t => t.status === "open" || t.status === "pending"); return t?.user?.name || "-"; })()} arrow>
                                            <span>
                                                {(() => {
                                                    const openTicket = contact.tickets?.find(t => t.status === "open" || t.status === "pending");
                                                    return openTicket?.user?.name || "-";
                                                })()}
                                            </span>
                                        </AppTooltip>
                                    </TableCell>
                                    <TableCell align="center">
                                        <span
                                            style={{
                                                display: "inline-flex",
                                                alignItems: "center",
                                                gap: 6,
                                                padding: "4px 10px",
                                                borderRadius: 12,
                                                fontSize: 11,
                                                fontWeight: 700,
                                                textTransform: "uppercase",
                                                letterSpacing: 0.4,
                                                background: contact.active
                                                    ? "rgba(46,168,90,0.12)"
                                                    : (theme.mode === "dark" ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.06)"),
                                                color: contact.active
                                                    ? "#2EA85A"
                                                    : (theme.mode === "dark" ? "#8696a0" : "#667781"),
                                            }}
                                        >
                                            {contact.active ? (
                                                <CheckCircleIcon size={12} strokeWidth={2.5} />
                                            ) : (
                                                <CancelIcon size={12} strokeWidth={2.5} />
                                            )}
                                            {contact.active
                                                ? (i18n.t("contacts.status.active") || "Ativo")
                                                : (i18n.t("contacts.status.inactive") || "Inativo")}
                                        </span>
                                    </TableCell>
                                    <TableCell align="right" className={classes.actionsCell}>
                                        <AppTooltip title={i18n.t("contacts.buttons.startChat") || "Iniciar conversa"}>
                                            <IconButton
                                                size="small"
                                                className={classes.iconActionBtn}
                                                disabled={!contact.active}
                                                onClick={() => {
                                                    setContactTicket(contact);
                                                    setNewTicketModalOpen(true);
                                                }}
                                            >
                                                {contact.channel === "whatsapp" && (<WhatsAppIcon size={16} strokeWidth={2} />)}
                                                {contact.channel === "instagram" && (<InstagramIcon size={16} strokeWidth={2} />)}
                                                {contact.channel === "facebook" && (<FacebookIcon size={16} strokeWidth={2} />)}
                                            </IconButton>
                                        </AppTooltip>
                                        <AppTooltip title={i18n.t("contacts.buttons.edit") || "Editar"}>
                                            <IconButton
                                                size="small"
                                                className={classes.iconActionBtn}
                                                onClick={() => hadleEditContact(contact.id)}
                                            >
                                                <EditIcon size={16} strokeWidth={2} />
                                            </IconButton>
                                        </AppTooltip>
                                        <AppTooltip title={contact.active ? (i18n.t("contacts.buttons.block") || "Bloquear") : (i18n.t("contacts.buttons.unblock") || "Desbloquear")}>
                                            <IconButton
                                                size="small"
                                                className={classes.iconActionBtn}
                                                onClick={
                                                    contact.active
                                                        ? () => { setConfirmOpen(true); setBlockingContact(contact); }
                                                        : () => { setConfirmOpen(true); setUnBlockingContact(contact); }
                                                }
                                            >
                                                {contact.active ? (
                                                    <BlockIcon size={16} strokeWidth={2} />
                                                ) : (
                                                    <CheckCircleIcon size={16} strokeWidth={2} />
                                                )}
                                            </IconButton>
                                        </AppTooltip>
                                        <Can
                                            role={user.profile}
                                            perform="contacts-page:deleteContact"
                                            yes={() => (
                                                <AppTooltip title={i18n.t("contacts.buttons.delete") || "Excluir"}>
                                                    <IconButton
                                                        size="small"
                                                        className={classes.deleteActionBtn}
                                                        onClick={(e) => { setConfirmOpen(true); setDeletingContact(contact); }}
                                                    >
                                                        <DeleteOutlineIcon size={16} strokeWidth={2} />
                                                    </IconButton>
                                                </AppTooltip>
                                            )}
                                        />
                                    </TableCell>
                                </TableRow>
                            ))}
                            {loading && <TableRowSkeleton avatar columns={10} />}
                        </>
                    </TableBody>
                </Table>
                </div>
                )}
            </Paper>
        </MainContainer >
    );
};

export default Contacts;
